import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
// removed react-query for now; use simple axios fetch
import axios from "axios";
import { toast } from "react-hot-toast";
import { useUserContext } from "../../context/UserContext";
const formatDate = (dateStr) => {
  if (!dateStr) return "";
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  });
};

export default function FilterConfsDashboard() {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [products, setProducts] = useState([]);
  const [page, setPage] = useState(1);
  const pageSize = 23;
  const [showAddMenu, setShowAddMenu] = useState(false);
  // Removed unused rawResponse state
  // loading/error managed by react-query
  const { token, logout } = useUserContext();

  // Force local API for testing
  const API_BASE = process.env.REACT_APP_API_URL;

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await axios.get(`${API_BASE}/filterconf/inventory`, { headers });
      const data = res.data?.inventory ?? res.data?.data?.data ?? res.data?.data ?? res.data;
      const normalized = (Array.isArray(data) ? data : [])
        .map((p) => ({
          id: p.product_id || p.productId || p.id,
          model: p.product_name || p.device_model || p.model || p.deviceModel || "",
          deviceId: p.device_id || p.deviceId || "",
          customerName: p.customer_name || p.customerName || "",
          ro: p.ro_mem_id || p.ro_id || p.ro || (p.ro_mem && p.ro_mem.component_id) || "",
          alk: p.alkaline_id || p.alk_id || p.alk || (p.alkaline && p.alkaline.component_id) || "",
          carbon: p.carbon_id || p.carbon || (p.carbon && p.carbon.component_id) || "",
          sed: p.sed_id || p.sediment_id || p.sed || (p.sed && p.sed.component_id) || "",
          ro_at: p.ro_mem?.installed_at || "",
          alk_at: p.alkaline?.installed_at || "",
          carbon_at: p.carbon?.installed_at || "",
          sed_at: p.sed?.installed_at || "",
          raw: p,
        }))
        .filter(Boolean);
      setProducts(normalized);
      return { products: normalized, raw: res.data };
    } catch (err) {
      setError("Failed to load products");
      if (err.response?.status === 401) {
        logout();
        toast.error('Session expired. Please login again.');
      }
    } finally {
      setLoading(false);
    }
  }, [token, API_BASE, navigate, logout]);

  // Use simple axios + effect fetch for now
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");



  // data is fetched via react-query (useQuery) so no imperative fetch on mount required

  useEffect(() => {
    const handleRefresh = () => {
      fetchProducts();
    };
    window.addEventListener("filterConfs:refresh", handleRefresh);
    return () => {
      window.removeEventListener("filterConfs:refresh", handleRefresh);
    };
  }, [fetchProducts]);

  useEffect(() => {
    // Fetch initial data on mount
    fetchProducts();
  }, [fetchProducts]);

  // Reset page to 1 when products or search changes
  useEffect(() => {
    setPage(1);
  }, [products, searchTerm]);

  // Compute filtered products and pagination details
  const filteredProducts = useMemo(() => {
    let filtered = products.filter((prod) =>
      searchTerm
        ? (prod.id || "").toLowerCase().includes(searchTerm.toLowerCase()) || (prod.model || "").toLowerCase().includes(searchTerm.toLowerCase()) || (prod.deviceId || "").toLowerCase().includes(searchTerm.toLowerCase())
        : true
    );

    // Sort by latest installation date (most recent first)
    filtered.sort((a, b) => {
      const getLatestInstallDate = (product) => {
        const dates = [product.ro_at, product.alk_at, product.carbon_at, product.sed_at]
          .filter(date => date) // Remove empty dates
          .map(date => new Date(date).getTime()); // Convert to timestamps
        return dates.length > 0 ? Math.max(...dates) : 0; // Return latest timestamp or 0 if no dates
      };

      const dateA = getLatestInstallDate(a);
      const dateB = getLatestInstallDate(b);

      return dateB - dateA; // Latest first (descending order)
    });

    return filtered;
  }, [products, searchTerm]);

  const totalPages = Math.max(1, Math.ceil(filteredProducts.length / pageSize));
  const visibleProducts = useMemo(() => {
    const s = (page - 1) * pageSize;
    return filteredProducts.slice(s, s + pageSize);
  }, [filteredProducts, page, pageSize]);

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  return (
    <div className="w-full min-h-full px-3 py-6 sm:px-6 md:px-8 max-w-7xl mx-auto">
      <div className="bg-white rounded-xl shadow-md border border-gray-100 p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row justify-between items-stretch sm:items-center gap-4 mb-6">
          <h1 className="text-xl sm:text-2xl font-bold text-gray-800">Product Inventory</h1>
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 items-stretch sm:items-center">
            <div className="relative flex-1 sm:w-64">
              <input
                type="text"
                placeholder="Search..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <svg
                className="w-5 h-5 text-gray-400 absolute left-3 top-2.5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <div className="relative inline-flex items-center shrink-0">
              <button
                onClick={() => navigate("/filter-confs/add")}
                className="px-4 py-2 bg-indigo-600 text-white rounded-l-lg hover:bg-indigo-700 transition-colors text-sm font-medium"
              >
                Add Config
              </button>
              <button
                aria-label="Open add config menu"
                onClick={(e) => { e.stopPropagation(); setShowAddMenu((s) => !s); }}
                className="px-3 py-2 bg-indigo-50 border border-l-0 rounded-r-lg hover:bg-indigo-100 text-sm"
              >
                ▾
              </button>
              {showAddMenu && (
                <div className="absolute right-0 top-full z-20 mt-1 w-44 bg-white shadow-xl rounded-lg border border-gray-200">
                  <button
                    className="w-full text-left px-3 py-2 hover:bg-indigo-50 hover:text-indigo-600 text-sm transition-colors"
                    onClick={() => { setShowAddMenu(false); navigate('/filter-confs/add/alk'); }}
                  >
                    Add Alk Config
                  </button>
                  <button
                    className="w-full text-left px-3 py-2 hover:bg-indigo-50 hover:text-indigo-600 text-sm transition-colors"
                    onClick={() => { setShowAddMenu(false); navigate('/filter-confs/add/ro'); }}
                  >
                    Add RO Config
                  </button>
                  <button
                    className="w-full text-left px-3 py-2 hover:bg-indigo-50 hover:text-indigo-600 text-sm transition-colors"
                    onClick={() => { setShowAddMenu(false); navigate('/filter-confs/add/sediment'); }}
                  >
                    Add Sediment Config
                  </button>
                  <button
                    className="w-full text-left px-3 py-2 hover:bg-indigo-50 hover:text-indigo-600 text-sm transition-colors"
                    onClick={() => { setShowAddMenu(false); navigate('/filter-confs/add/carbon'); }}
                  >
                    Add Carbon Config
                  </button>
                </div>
              )}
            </div>

          </div>
        </div>

        <div className="overflow-x-auto w-full">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-gray-100 border-b border-gray-200">
                <th className="p-4 font-semibold text-gray-600">Replace</th>
                <th className="p-4 font-semibold text-gray-600">Product ID</th>
                <th className="p-4 font-semibold text-gray-600">Device Model</th>
                <th className="p-4 font-semibold text-gray-600">Device ID</th>
                <th className="p-4 font-semibold text-gray-600">Customer Name</th>
                <th className="p-4 font-semibold text-gray-600">RO Mem ID</th>
                <th className="p-4 font-semibold text-gray-600">Alkaline ID</th>
                <th className="p-4 font-semibold text-gray-600">Carbon Filter ID</th>
                <th className="p-4 font-semibold text-gray-600">Sed Filter ID</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={9} className="p-4 text-gray-500">Loading...</td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan={9} className="p-4 text-red-500">{error}</td>
                </tr>
              ) : products.length === 0 ? (
                <tr>
                  <td colSpan={9} className="p-4 text-gray-500">No products found</td>
                </tr>
              ) : (
                <>
                  {visibleProducts.map((product, index) => (
                    <tr
                      key={index}
                      className="border-b border-gray-100 hover:bg-gray-50"
                    >
                      <td className="p-4">
                        <button
                          type="button"
                          className="px-3 py-2 bg-indigo-600 text-white text-sm font-semibold rounded hover:bg-indigo-700"
                          onClick={() => {
                            navigate(`/filter-confs/details/${product.id}`, {
                              state: { product: product.raw || product, tab: "replace" },
                            });
                          }}
                        >
                          Replace
                        </button>
                      </td>
                      <td
                        className="p-4 text-gray-700 cursor-pointer"
                        onClick={() => navigate(`/filter-confs/details/${product.id}`, { state: { product: product.raw || product } })}
                      >{product.id}</td>
                      <td className="p-4 text-gray-700">{product.model}</td>
                      <td className="p-4 text-gray-700">{product.deviceId || "-"}</td>
                      <td className="p-4 text-gray-700">{product.customerName || "-"}</td>
                      <td className="p-4 text-gray-700 align-top">
                        <div className="font-semibold text-gray-900 mb-1">{product.ro}</div>
                        {product.ro_at && <div className="text-sm text-gray-600 font-mono whitespace-nowrap bg-gray-100 inline-block px-2 py-1 rounded"><span className="text-gray-500 mr-1 text-xs uppercase tracking-wider">Installed:</span><br />{formatDate(product.ro_at)}</div>}
                      </td>
                      <td className="p-4 text-gray-700 align-top">
                        <div className="font-semibold text-gray-900 mb-1">{product.alk}</div>
                        {product.alk_at && <div className="text-sm text-gray-600 font-mono whitespace-nowrap bg-gray-100 inline-block px-2 py-1 rounded"><span className="text-gray-500 mr-1 text-xs uppercase tracking-wider">Installed:</span><br />{formatDate(product.alk_at)}</div>}
                      </td>
                      <td className="p-4 text-gray-700 align-top">
                        <div className="font-semibold text-gray-900 mb-1">{product.carbon}</div>
                        {product.carbon_at && <div className="text-sm text-gray-600 font-mono whitespace-nowrap bg-gray-100 inline-block px-2 py-1 rounded"><span className="text-gray-500 mr-1 text-xs uppercase tracking-wider">Installed:</span><br />{formatDate(product.carbon_at)}</div>}
                      </td>
                      <td className="p-4 text-gray-700 align-top">
                        <div className="font-semibold text-gray-900 mb-1">{product.sed}</div>
                        {product.sed_at && <div className="text-sm text-gray-600 font-mono whitespace-nowrap bg-gray-100 inline-block px-2 py-1 rounded"><span className="text-gray-500 mr-1 text-xs uppercase tracking-wider">Installed:</span><br />{formatDate(product.sed_at)}</div>}
                      </td>
                    </tr>
                  ))}
                </>
              )}
            </tbody>
          </table>
        </div>
        <div className="flex flex-wrap justify-between sm:justify-end items-center mt-6 gap-2 pt-2">
          <span className="text-gray-600 text-sm">Page {page} of {totalPages}</span>
          <button
            className="p-2 border rounded hover:bg-gray-100"
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            aria-label="Previous page"
          >
            &lt;
          </button>
          <button
            className="p-2 border rounded hover:bg-gray-100 bg-indigo-50 text-indigo-600"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            aria-label="Next page"
          >
            &gt;
          </button>
          <button
            className="p-2 border rounded hover:bg-gray-100"
            disabled={page >= totalPages}
            onClick={() => setPage(totalPages)}
            aria-label="Last page"
          >
            &gt;|
          </button>
        </div>
      </div>
    </div>
  );
}
